'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import Script from 'next/script';
import { FB_PIXEL_ID, initFacebookPixel, trackFacebookPageView } from '@/utils/facebookPixel';

/**
 * Facebook Pixel Component
 * Performance-optimized implementation with automatic page view tracking
 *
 * Features:
 * - Lazy loading (doesn't block initial page render)
 * - Automatic page view tracking on route changes
 * - Development mode logging
 * - Respects user privacy (checks for pixel ID)
 */
export default function FacebookPixel() {
  const pathname = usePathname();

  // Track page views on route changes
  useEffect(() => {
    if (!FB_PIXEL_ID) return;

    // Track page view
    trackFacebookPageView();
  }, [pathname]);

  // Don't render if no Pixel ID is configured
  if (!FB_PIXEL_ID) {
    // Commented out: No Facebook Pixel configured
    // if (process.env.NODE_ENV === 'development') {
    //   console.warn(
    //     'Facebook Pixel: NEXT_PUBLIC_FB_PIXEL_ID not configured. Add it to .env.local to enable tracking.'
    //   );
    // }
    return null;
  }

  return (
    <>
      {/* Facebook Pixel Script - Loaded with lazyOnload for zero performance impact */}
      <Script
        id="facebook-pixel"
        strategy="lazyOnload"
        dangerouslySetInnerHTML={{
          __html: `
            !function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', '${FB_PIXEL_ID}');
            fbq('track', 'PageView');
          `,
        }}
        onLoad={() => {
          // Initialize pixel utilities after script loads
          initFacebookPixel();

          if (process.env.NODE_ENV === 'development') {
            console.log('Facebook Pixel loaded successfully');
          }
        }}
      />

      {/* Facebook Pixel Noscript Fallback */}
      <noscript>
        <img
          height="1"
          width="1"
          style={{ display: 'none' }}
          src={`https://www.facebook.com/tr?id=${FB_PIXEL_ID}&ev=PageView&noscript=1`}
          alt=""
        />
      </noscript>
    </>
  );
}
